import React, {memo} from 'react';
import styled from 'styled-components';
import {
   ControlButton,
   Glyphs,
   Label,
   MenuItem,
   PopoverButton,
   TextInput,
   TscButtonGlyphPlacement,
   TscButtonType,
   TscPopoverOrigin,
   TscStyles,
   TscThemeName,
   TscThemeNames
} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import SparkleGlyph from '../../static/svg/SparkleGlyph.svg';
import {useTscTheme} from '../../context/themeProvider';
import {EntitlementType} from '../../model/userQuotas';
import {useUserQuotaStore} from '../../context/userQuotaStore';
import Constants from '../../constants/Constants';
import CssUtil from '../../util/CssUtil';
import {CaptioningState} from '../../model/captionModels';
import {IChaptersMinMax} from '../../model/mediaIntelligence';
import config from '../../service/config';

export enum MediaDetailGenerationTypes {
   title = 'title',
   description = 'description',
   chapters = 'chapters'
}

const MediaDetailsGenerateContainer = styled.div<{theme: TscThemeNames}>`
   position: relative;
   display: flex;
   align-items: center;
   border-radius: ${TscStyles.border.radius.xl};
   border: 2px solid ${TscStyles.color.message.caution};
   background: ${props => props.theme === TscThemeName.dawn ? TscStyles.color.ui.dawn.light : TscStyles.color.ui.dusk.dark};
   box-shadow: 0 2px 16px 0 rgba(235, 174, 33, 0.30);
   padding: 0.5rem;
   margin-bottom: .5rem;
   box-sizing: border-box;

   .sparkleGlyph {
      flex: 0 0 auto;
      align-self: baseline;
      margin-right: .5rem;
   }

   .generate-button-options {
      flex-shrink: 0;
   }
`;

const BetaTag = styled.div`
   position: absolute;
   right: -12px;
   top: -10px;
   display: inline-flex;
   height: 16px;
   padding: 0 12px;
   justify-content: center;
   align-items: center;
   flex-shrink: 0;
   font-size: 0.8125rem;
   font-style: normal;
   font-weight: 600;
   line-height: 20px;
   color: ${TscStyles.color.text.dark};
   border-radius: 27px;
   background: linear-gradient(90deg, ${CssUtil.toRgba(TscStyles.color.action.screencastPrimaryStop1, 1)}, ${CssUtil.toRgba(TscStyles.color.action.screencastPrimaryStop2, 1)});
`;

const GenerationDescriptionContainer = styled.div`
   font-size: 0.8125rem;
   font-weight: ${TscStyles.font.weight.semibold};
   padding-right: 0.25rem;
`;

export const GenerationOptionsMenu = styled.div`
   display: flex;
   flex-direction: column;
   padding: .125rem;
`;

const MinMax = styled.div`
   color: ${TscStyles.color.text.dark};
   display: flex;
   justify-content: space-around;
   margin-bottom: 1rem;
`;

const {minimumGeneratedChapters, maximumChapters} = Constants.chapters;

export const MediaDetailsGenerateMenuBase: React.FC<IMediaDetailsGenerateMenuProps> = ({captionState, minMax, canModifyMinMax, setMinMaxCallback, onGenerateCallback, onStartTranscriptionCallback}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const quotaEntitlementType = useUserQuotaStore(s => s.QuotaEntitlementType);
   const getGenerateOptions = (): React.JSX.Element => (
      <GenerationOptionsMenu>
         <MenuItem
            onClick={() => {
               onGenerateCallback(MediaDetailGenerationTypes.title);
            }}
            label={t('details.generate.generateOptions.title')}
         />
         <MenuItem
            onClick={() => {
               onGenerateCallback(MediaDetailGenerationTypes.description);
            }}
            label={t('details.generate.generateOptions.description')}
         />
         <MenuItem
            onClick={() => {
               onGenerateCallback(MediaDetailGenerationTypes.chapters);
            }}
            label={t('details.generate.generateOptions.chapters')}
            isDisabled={false}
         />
         {canModifyMinMax && (
            <MinMax onClick={e => e.stopPropagation()}>
               <div>
                  <Label
                     sharedId="min"
                     labelText="Min"
                  />
                  <TextInput
                     sharedId="min"
                     type="number"
                     minNumValue={minimumGeneratedChapters}
                     maxNumValue={maximumChapters}
                     value={`${minMax.min}`}
                     onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinMaxCallback({min: parseInt(e.target.value, 10), max: minMax.max})}
                  />
               </div>
               <div>
                  <Label
                     sharedId="max"
                     labelText="Max"
                  />
                  <TextInput
                     sharedId="max"
                     type="number"
                     minNumValue={minimumGeneratedChapters}
                     maxNumValue={maximumChapters}
                     value={`${minMax.max}`}
                     onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinMaxCallback({min: minMax.min, max: parseInt(e.target.value, 10)})}
                  />
               </div>
            </MinMax>
         )}
      </GenerationOptionsMenu>
   );

   const getCallToActionButton = (): React.JSX.Element => {
      if (quotaEntitlementType === EntitlementType.free) {
         return (
            <ControlButton
               buttonType={TscButtonType.primaryScreencast}
               label={t('details.generate.upgradeCallToAction')}
               href={config.environmentData.ScreencastProPurchaseLinkUrl}
               openLinkInNewTab={true}
               testId="upgrade-tier-button-link"
            />
         );
      } else if (captionState === CaptioningState.None) {
         return (
            <ControlButton
               buttonType={TscButtonType.secondary}
               label={t('details.generate.transcribeButtonLabel')}
               testId="transcribe-video-button"
               onClick={() => {
                  onStartTranscriptionCallback();
               }}
            />
         );
      } else if (captionState === CaptioningState.Published) {
         return (
            <PopoverButton
               buttonType={TscButtonType.secondary}
               buttonText={t('details.generate.buttonLabel')}
               buttonTitle={t('details.generate.buttonLabel')}
               buttonGlyph={<Glyphs.CaratDown16x16 />}
               glyphPlacement={TscButtonGlyphPlacement.right}
               origin={TscPopoverOrigin.bottomLeft}
               className="generate-button-options"
               testId="generate-options-button"
               popoverMenu={getGenerateOptions()}
               closeMenuOnClick={true}
            />
         );
      }
      return null;
   };

   const getDescriptionText = (): string => {
      if (captionState === CaptioningState.Published || quotaEntitlementType === EntitlementType.free) {
         return t('details.generate.description');
      } else if (captionState === CaptioningState.None) {
         return t('details.generate.needToTranscribeMessage');
      }
      return t('details.generate.transcribingMessage');
   };

   return (
      <MediaDetailsGenerateContainer theme={theme}>
         <SparkleGlyph className="sparkleGlyph" />
         <GenerationDescriptionContainer>
            {getDescriptionText()}
         </GenerationDescriptionContainer>
         {getCallToActionButton()}
         <BetaTag>
            {t('details.generate.betaTag')}
         </BetaTag>
      </MediaDetailsGenerateContainer>
   );
};

export interface IMediaDetailsGenerateMenuProps {
   captionState: CaptioningState;
   minMax: IChaptersMinMax;
   canModifyMinMax: boolean;
   setMinMaxCallback(minMax: IChaptersMinMax): void;
   onGenerateCallback(generateType: MediaDetailGenerationTypes): void;
   onStartTranscriptionCallback(): void;
}

export default memo(MediaDetailsGenerateMenuBase);
